import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { MatterPackage } from '@models/matter-package';
import { Store } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { MatterPackagesDispatch } from '@state/matter/matter-packages/matter-packages.dispatch';
import { MatterPackagesSelectors } from '@state/matter/matter-packages/matter-packages.selectors';
import { MatterWordDocumentsDispatch } from '@state/matter/matter-word-documents/matter-word-documents.dispatch';
import { CreateStatusType } from '@state/utils/models';
import { Subject, takeUntil } from 'rxjs';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { DocumentsDrawerComponent } from '../documents-drawer/documents-drawer.component';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './create-doc-package-drawer.hooks';

export enum ADD_DOC_OPTIONS {
	CREATE_PACKAGE = 'create-package',
	ADD_DOCS = 'add-docs',
	CREATE_BLANK_DOC = 'create-blank-doc',
}

@Component({
	selector: 'app-create-doc-package-drawer',
	templateUrl: './create-doc-package-drawer.component.html',
	imports: [CommonModule, IconsModule, TestHookDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDocPackageDrawerComponent implements OnInit, OnDestroy {
	testHooks = TestHooks;
	ADD_DOC_OPTIONS = ADD_DOC_OPTIONS;

	packageCreateStatus$ = this.store.select(
		MatterPackagesSelectors.getPackageCreateStatus,
	);

	isLoading = false;
	private destroy$ = new Subject<void>();

	constructor(
		private drawerService: DrawerService,
		private matterPackageDispatch: MatterPackagesDispatch,
		private matterWordDocumentDispath: MatterWordDocumentsDispatch,
		private navService: NavigationService,
		private analytics: AnalyticsService,
		private store: Store,
		private toastService: ToastService,
	) {}

	ngOnInit(): void {
		this.packageCreateStatus$
			.pipe(takeUntil(this.destroy$))
			.subscribe((status: CreateStatusType) => {
				if (status === CreateStatusType.failed) {
					this.isLoading = false;
					this.toastService.error(
						'Failed to create package. Please try again.',
					);
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onOptionClick(option: ADD_DOC_OPTIONS) {
		if (this.isLoading) return;

		switch (option) {
			case ADD_DOC_OPTIONS.CREATE_PACKAGE:
				this.isLoading = true;
				this.matterPackageDispatch.Create({
					packageName: 'New Package',
				} as MatterPackage);
				this.analytics.logEvent(AnalyticsEvent.MATTER_DOCUMENTS_ADD_PACKAGE);
				break;
			case ADD_DOC_OPTIONS.ADD_DOCS:
				this.drawerService.openDrawer(DocumentsDrawerComponent, {
					title: 'Add Documents',
					size: 'lg',
					noStyle: true,
					persist: true,
					data: {
						onDocumentSelected: () => {
							this.analytics.logEvent(
								AnalyticsEvent.MATTER_DOCUMENTS_ADD_DOCUMENT,
							);
							this.navService.updateQueryParams({ tab: 'my-documents' });
						},
					},
				});
				break;
			case ADD_DOC_OPTIONS.CREATE_BLANK_DOC:
				this.navService.updateQueryParams({ tab: 'my-documents' });
				this.matterWordDocumentDispath.Create({
					document: null,
				});
				break;
		}
	}
}
